import { MdKeyboardArrowRight } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";
import { useState, useEffect } from 'react';
import { FaUserCircle } from "react-icons/fa";
import { IoMdStar } from "react-icons/io";
import { BiSolidLike } from "react-icons/bi";
import { Paragraph } from "../../basic/title";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { likeReview } from "../../../../redux/slices/reviews";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";

const ReviewsSidebar = ({ title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [likedReviews, setLikedReviews] = useState({});

  const { reviews } = useSelector((state) => state.review);
  const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL;
  const dispatch = useDispatch();
  const { id } = useParams();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    document.body.style.overflow = !sidebarOpen ? 'hidden' : 'unset';
  };

  useEffect(() => {
    if(reviews?.length > 0) {
      const userData = localStorage.getItem("user");
      const parsedUser = userData ? JSON.parse(userData) : null;

      const likedState = {};
      reviews.forEach((rev) => {
        if(rev.liked_by?.includes(parsedUser?.id)) {
          likedState[rev.id] = true;
        }
      });
      setLikedReviews(likedState);
    }
  }, [reviews, dispatch]);

  const handleLike = async (reviewId) => {
    const userData = localStorage.getItem("user");
    const parsedUser = userData ? JSON.parse(userData) : null;

    if (!parsedUser?.id) {
      toast.error("Please log in to like/unlike a review.");
      return;
    }

    const payload = {
      review_id: reviewId,
      user_id: parsedUser.id,
      product_id: id,
    };

    try {
      dispatch(likeReview(payload, toast));
      setLikedReviews((prev) => ({
        ...prev,
        [reviewId]: !prev[reviewId],
      }));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <div
        className="p-3 bg-gray-100 rounded-md mt-4 flex items-center sm:justify-start justify-center cursor-pointer"
        onClick={toggleSidebar}
      >
        <h2 className="font-dm text-lg capitalize font-medium text-left text-[#00A762]">
          VIEW ALL REVIEWS
        </h2>
        <MdKeyboardArrowRight className="text-3xl text-[#00A762]" />
      </div>

      {sidebarOpen && (
        <>
          <div className="fixed top-0 left-0 z-40 w-full h-full bg-black opacity-50" onClick={toggleSidebar} />
          <div className={`fixed top-0 right-0 z-50 h-full md:w-[600px] w-full bg-white shadow-lg transition-transform duration-300 ease-in-out transform ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="p-4">
              <div className="p-3 bg-gray-100 rounded-md flex items-center justify-between">
                <h2 className="font-dm text-lg capitalize font-medium text-[#00A762]">{title}</h2>
                <div
                  className="flex items-center justify-center h-8 w-8 rounded-md bg-[#00A762] cursor-pointer"
                  onClick={toggleSidebar}
                >
                  <RxCross2 className="text-xl text-white" />
                </div>
              </div>

              <div className="max-h-[600px] overflow-y-auto mt-4 space-y-6 pr-2">
                {reviews?.length > 0 ? (
                  reviews.map((review, index) => {
                    let images = [];

                    try {
                      images = typeof review.images === 'string' ? JSON.parse(review.images) : review.images || [];
                    } catch (err) {
                      images = [];
                    }

                    return (
                      <div key={index} className="bg-gray-50 border border-gray-200 rounded-xl p-5 shadow-sm">
                        <div className="flex items-center space-x-4">
                          <FaUserCircle className="text-4xl text-[#00A762]" />
                          <div>
                            <h4 className="font-semibold text-[#072320] text-lg capitalize">
                              {review.firstname || review.username || "Anonymous"}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {dayjs(review.created_at).format("DD MMMM YYYY")}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center mt-3">
                          <span className={`flex items-center text-white text-sm font-semibold px-3 py-1 rounded-full ${
                            review.rating <= 2
                              ? "bg-red-500"
                              : review.rating === 3
                              ? "bg-yellow-500"
                              : "bg-green-600"
                          }`}>
                            {review.rating} <IoMdStar className="ml-1 text-base" />
                          </span>
                        </div>

                        <div className="mt-3 text-sm text-gray-700 leading-relaxed">
                          <Paragraph title={review.comment} textAlign="onyleft" />
                        </div>

                        {images.length > 0 && (
                          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {images.map((img, i) => (
                              <img
                                key={i}
                                src={`${BASE_IMAGE_URL}${img}`}
                                alt={`Review Image ${i + 1}`}
                                className="w-full h-24 object-cover rounded-md border"
                              />
                            ))}
                          </div>
                        )}

                        <div className="flex items-center justify-between mt-4">
                          <button
                            onClick={() => handleLike(review.id)}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition
                              ${likedReviews[review.id] ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-600"}
                              hover:bg-green-200`}
                          >
                            <BiSolidLike className={`text-base ${likedReviews[review.id] ? "text-green-600" : ""}`} />
                            {review.likes_count || 0}
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-500 text-center mt-10">No reviews yet for this product.</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ReviewsSidebar;